import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";

const CandidateTimeLineView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isfrom, returnTo } = location.state || {};

    const [timelineData, setTimelineData] = useState(location.state?.timelineData || []);
    const [loading, setLoading] = useState(!(Array.isArray(location.state?.timelineData) && location.state.timelineData.length > 0));

    const candidateName = timelineData[0]?.candidate_name || "N/A";

    useEffect(() => {
        const timeline = location.state?.timelineData;
        const logs = location.state?.actionLogs;

        if (timeline && timeline.length > 0) {
            setTimelineData(timeline);
        } else if (logs && logs.length > 0) {
            setTimelineData(logs);
        } else {
            setTimelineData([]);
        }
    }, [location.state]);

    useEffect(() => {
        if (Array.isArray(timelineData) && timelineData.length > 0) {
            setLoading(false);
        } else {
            setLoading(false); // even empty state should stop loading
        }
    }, [timelineData]);

    const navigateBack = () => {
        if (returnTo?.pathname) {
            navigate(returnTo.pathname, { state: returnTo.state });
            return;
        }
        if (isfrom) {
            navigate(isfrom);
            return;
        }
        navigate(-1);
    };

    // Color + icon + label config per action category (same visual language as JobTimeLineView)
    const EVENT_STYLES = {
        added:        { badge: "#185fa5", bg: "#e6f1fb", border: "#b5d4f4", title: "#0c447c", body: "#185fa5", icon: "fa-user-plus",       label: "Added" },
        updated:      { badge: "#639922", bg: "#eaf3de", border: "#c0dd97", title: "#27500a", body: "#3b6d11", icon: "fa-pencil",           label: "Updated" },
        scheduled:    { badge: "#d97706", bg: "#faeeda", border: "#fac775", title: "#633806", body: "#854f0b", icon: "fa-calendar",         label: "Scheduled" },
        completed:    { badge: "#534ab7", bg: "#eeedfe", border: "#cecbf6", title: "#3c3489", body: "#534ab7", icon: "fa-check-circle-o",   label: "Completed" },
        qualified:    { badge: "#3aa757", bg: "#e6f6ea", border: "#b3e6c1", title: "#1c5d30", body: "#2f8c48", icon: "fa-thumbs-up",        label: "Qualified" },
        disqualified: { badge: "#e24b4a", bg: "#fcebeb", border: "#f7c1c1", title: "#791f1f", body: "#a32d2d", icon: "fa-thumbs-down",      label: "Disqualified" },
        removed:      { badge: "#d85a30", bg: "#faece7", border: "#f5c4b3", title: "#712b13", body: "#993c1d", icon: "fa-user-times",       label: "Removed" },
        offer:        { badge: "#0e9488", bg: "#e3f5f3", border: "#aee1db", title: "#0a5850", body: "#0e9488", icon: "fa-file-text-o",      label: "Offer" },
        rejected:     { badge: "#e24b4a", bg: "#fcebeb", border: "#f7c1c1", title: "#791f1f", body: "#a32d2d", icon: "fa-times-circle-o",   label: "Rejected" },
        placed:       { badge: "#2f9e44", bg: "#e6f6ea", border: "#b3e6c1", title: "#1c5d30", body: "#2f8c48", icon: "fa-check-square-o",   label: "Placed" },
        hrms:         { badge: "#0d6efd", bg: "#e8f0fe", border: "#b6d0fb", title: "#0a3d8f", body: "#1257c9", icon: "fa-exchange",         label: "HRMS" },
        default:      { badge: "#5f5e5a", bg: "#f1efe8", border: "#d3d1c7", title: "#444441", body: "#5f5e5a", icon: "fa-circle-o",         label: "Update" },
    };

    const getEventStyle = (actionType) => {
        const t = String(actionType || "").toLowerCase();

        if (t.includes("hrms")) return EVENT_STYLES.hrms;
        if (t.includes("placed")) return EVENT_STYLES.placed;

        if (t.includes("offer")) {
            if (t.includes("rejected")) return EVENT_STYLES.rejected;
            return EVENT_STYLES.offer;
        }

        if (t.includes("disqualified")) return EVENT_STYLES.disqualified;
        if (t.includes("qualified")) return EVENT_STYLES.qualified;

        if (t.includes("removed")) return EVENT_STYLES.removed;

        if (t.includes("scheduled")) return EVENT_STYLES.scheduled;
        if (t.includes("completed")) return EVENT_STYLES.completed;

        if (t.includes("added")) return EVENT_STYLES.added;
        if (t.includes("updated") || t.includes("edit")) return EVENT_STYLES.updated;

        return EVENT_STYLES.default;
    };

    const getInitials = (name) => {
        if (!name) return "--";
        const parts = String(name).trim().split(/\s+/);
        return parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join("");
    };

    return (
        <Container fluid className="page-content">
            {loading ? (
                <div className="w-100 text-center d-flex align-items-center justify-content-center" style={{ height: "80vh" }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="page-title-box mb-0 recruiter-dashboard">
                    <Row className="d-flex justify-content-between align-items-center mb-3">
                        <Col xs="auto">
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div
                                    aria-hidden="true"
                                    style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 10,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#fff",
                                        background: "linear-gradient(135deg, #0e527d, #168b9b)",
                                        boxShadow: "0 5px 12px rgba(14, 82, 125, 0.18)",
                                        fontSize: 17,
                                    }}
                                >
                                    <i className="mdi mdi-account-clock-outline" />
                                </div>
                                <div>
                                    <h5 style={{ margin: 0, fontWeight: 700, color: "#102a43", letterSpacing: "-0.2px" }}>
                                        {candidateName} Timeline
                                    </h5>
                                    <div style={{ marginTop: 4, fontSize: 12, color: "#718096" }}>
                                        Activity history for this candidate
                                    </div>
                                </div>
                                <span
                                    style={{
                                        marginLeft: 4,
                                        padding: "5px 9px",
                                        borderRadius: 999,
                                        background: "#e6f6f7",
                                        color: "#087f8c",
                                        fontSize: 12,
                                        fontWeight: 700,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {timelineData.length} action{timelineData.length !== 1 ? "s" : ""}
                                </span>
                            </div>
                        </Col>
                        <Col xs="auto">
                            <button onClick={navigateBack} className="btn btn-primary">
                                <i className="fa fa-arrow-left me-2" />
                                Back
                            </button>
                        </Col>
                    </Row>

                    <Row className="justify-content-center">
                        <Col xxl={9} lg={11} xs={12}>
                            <Card
                                className="shadow-sm border-0"
                                style={{ borderRadius: 14, padding: "34px 28px", background: "#fff" }}
                            >
                                {timelineData.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                                        No timeline events found for this candidate.
                                    </div>
                                ) : (
                                    <div style={{ position: "relative" }}>
                                        {/* Center connector line */}
                                        <div
                                            style={{
                                                position: "absolute",
                                                left: "50%",
                                                top: 6,
                                                bottom: 6,
                                                width: 3,
                                                background:
                                                    "linear-gradient(180deg, #85b7eb 0%, #97c459 35%, #f0997b 65%, #f09595 100%)",
                                                borderRadius: 3,
                                                transform: "translateX(-1.5px)",
                                                opacity: 0.5,
                                            }}
                                        />

                                        {timelineData.map((log, index) => {
                                            const style = getEventStyle(log.action_type);
                                            const isLeft = index % 2 === 0;
                                            const isLast = index === timelineData.length - 1;

                                            const CardBlock = (
                                                <div
                                                    style={{
                                                        display: "inline-block",
                                                        background: style.bg,
                                                        border: `0.5px solid ${style.border}`,
                                                        borderRadius: 12,
                                                        padding: "12px 16px",
                                                        textAlign: "left",
                                                        maxWidth: 280,
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            background: style.badge,
                                                            color: "#fff",
                                                            borderRadius: 999,
                                                            padding: "2px 9px",
                                                            fontSize: 10,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {style.label}
                                                    </span>
                                                    <div style={{ fontSize: 13.5, fontWeight: 700, color: style.title, margin: "6px 0 2px" }}>
                                                        {log.action_type || "--"}
                                                    </div>
                                                    {log.description && (
                                                        <div style={{ fontSize: 11.5, color: style.body, lineHeight: 1.5 }}>
                                                            {log.description}
                                                        </div>
                                                    )}
                                                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10 }}>
                                                        <div
                                                            style={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: "50%",
                                                                background: style.badge,
                                                                color: "#fff",
                                                                fontSize: 9,
                                                                fontWeight: 700,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {getInitials(log.actioned_by_name)}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: style.title, fontWeight: 600 }}>
                                                            {log.actioned_by_name || "--"}
                                                        </div>
                                                    </div>
                                                    <div style={{ fontSize: 10.5, color: style.body, marginTop: 1 }}>
                                                        {log.action_date || "--"}
                                                    </div>
                                                </div>
                                            );

                                            return (
                                                <div
                                                    key={index}
                                                    style={{
                                                        position: "relative",
                                                        display: "grid",
                                                        gridTemplateColumns: "1fr 44px 1fr",
                                                        marginBottom: isLast ? 0 : 22,
                                                    }}
                                                >
                                                    <div style={{ gridColumn: 1, paddingRight: isLeft ? 20 : 0, textAlign: "right" }}>
                                                        {isLeft && CardBlock}
                                                    </div>

                                                    <div style={{ gridColumn: 2, display: "flex", justifyContent: "center" }}>
                                                        <div
                                                            style={{
                                                                width: 32,
                                                                height: 32,
                                                                borderRadius: "50%",
                                                                background: style.badge,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                zIndex: 1,
                                                                boxShadow: "0 0 0 4px #fff",
                                                            }}
                                                        >
                                                            <i className={`fa ${style.icon}`} style={{ fontSize: 14, color: "#fff" }} />
                                                        </div>
                                                    </div>

                                                    <div style={{ gridColumn: 3, paddingLeft: !isLeft ? 20 : 0 }}>
                                                        {!isLeft && CardBlock}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </div>
            )}
        </Container>
    );
};

export default CandidateTimeLineView;
